// ATT&CK v19 placement (DESIGN_SPEC §12.13). Each technique appears once, under its primary
// tactic, with the other tactics as "also …". Defense Evasion is retired in v19: its ids sit under
// Stealth (TA0005); T1562 is shown as emitted under Defense Impairment (TA0112) with a dagger.
//   attackTable(tags) → { tactics: [{ id, name, techs: [{ id, name, href, dagger, also, rules, maxSev }] }], techniqueCount }
//   techniqueInfo(id) → { name, tactics, dagger } | undefined   ·   techniqueHref(id)
import type { Severity, Tag } from "@/types";

const TACTICS = [
  ["TA0001", "Initial Access"],
  ["TA0002", "Execution"],
  ["TA0003", "Persistence"],
  ["TA0004", "Privilege Escalation"],
  ["TA0005", "Stealth"],
  ["TA0112", "Defense Impairment"],
  ["TA0006", "Credential Access"],
  ["TA0007", "Discovery"],
  ["TA0008", "Lateral Movement"],
  ["TA0009", "Collection"],
  ["TA0011", "Command and Control"],
  ["TA0010", "Exfiltration"],
  ["TA0040", "Impact"],
] as const;

type TacticName = (typeof TACTICS)[number][1];

type Info = { name: string; tactics: readonly TacticName[]; dagger?: boolean };

const EXEC = ["Execution"] as const;
const PERSIST_PRIV = ["Persistence", "Privilege Escalation"] as const;
const STEALTH = ["Stealth"] as const;
const DISC = ["Discovery"] as const;
const C2 = ["Command and Control"] as const;
const CRED = ["Credential Access"] as const;
const IMPACT = ["Impact"] as const;
const IMPAIR = ["Defense Impairment"] as const;

const TECHNIQUES: Readonly<Record<string, Info>> = {
  T1204: { name: "User Execution", tactics: EXEC },
  "T1204.002": { name: "Malicious File", tactics: EXEC },
  T1059: { name: "Command and Scripting Interpreter", tactics: EXEC },
  "T1059.001": { name: "PowerShell", tactics: EXEC },
  T1047: { name: "Windows Management Instrumentation", tactics: EXEC },
  "T1053.005": { name: "Scheduled Task", tactics: ["Execution", "Persistence", "Privilege Escalation"] },
  "T1547.001": { name: "Registry Run Keys / Startup Folder", tactics: PERSIST_PRIV },
  "T1543.003": { name: "Windows Service", tactics: PERSIST_PRIV },
  "T1546.012": { name: "Image File Execution Options Injection", tactics: PERSIST_PRIV },
  T1197: { name: "BITS Jobs", tactics: ["Stealth", "Persistence"] },
  "T1574.002": { name: "DLL Side-Loading", tactics: ["Persistence", "Privilege Escalation", "Stealth"], dagger: true },
  T1055: { name: "Process Injection", tactics: ["Stealth", "Privilege Escalation"] },
  "T1548.002": { name: "Bypass User Account Control", tactics: ["Privilege Escalation", "Stealth"] },
  T1027: { name: "Obfuscated Files or Information", tactics: STEALTH },
  T1140: { name: "Deobfuscate/Decode Files or Information", tactics: STEALTH },
  "T1036.005": { name: "Match Legitimate Resource Name or Location", tactics: STEALTH },
  "T1070.001": { name: "Clear Windows Event Logs", tactics: STEALTH },
  "T1218.005": { name: "Mshta", tactics: STEALTH },
  "T1218.010": { name: "Regsvr32", tactics: STEALTH },
  "T1218.011": { name: "Rundll32", tactics: STEALTH },
  "T1497.001": { name: "System Checks", tactics: ["Stealth", "Discovery"] },
  "T1497.003": { name: "Time Based Checks", tactics: ["Stealth", "Discovery"] },
  T1562: { name: "Impair Defenses", tactics: IMPAIR, dagger: true },
  "T1562.001": { name: "Disable or Modify Tools", tactics: IMPAIR, dagger: true },
  "T1562.004": { name: "Disable or Modify System Firewall", tactics: IMPAIR, dagger: true },
  "T1003.001": { name: "LSASS Memory", tactics: CRED },
  "T1555.003": { name: "Credentials from Web Browsers", tactics: CRED },
  "T1552.005": { name: "Cloud Instance Metadata API", tactics: CRED },
  T1016: { name: "System Network Configuration Discovery", tactics: DISC },
  T1033: { name: "System Owner/User Discovery", tactics: DISC },
  T1049: { name: "System Network Connections Discovery", tactics: DISC },
  T1082: { name: "System Information Discovery", tactics: DISC },
  "T1087.001": { name: "Local Account", tactics: DISC },
  T1135: { name: "Network Share Discovery", tactics: DISC },
  "T1071.001": { name: "Web Protocols", tactics: C2 },
  "T1071.004": { name: "DNS", tactics: C2 },
  T1105: { name: "Ingress Tool Transfer", tactics: C2 },
  "T1090.003": { name: "Multi-hop Proxy", tactics: C2 },
  "T1568.002": { name: "Domain Generation Algorithms", tactics: C2 },
  T1571: { name: "Non-Standard Port", tactics: C2 },
  T1486: { name: "Data Encrypted for Impact", tactics: IMPACT },
  T1490: { name: "Inhibit System Recovery", tactics: IMPACT },
  "T1566.001": { name: "Spearphishing Attachment", tactics: ["Initial Access"] },
};

/** Technique ids as the rules emit them: "T1055", "T1053.005" (case and spaces normalised). */
export const normTech = (id: string): string => id.trim().toUpperCase();

/** https://attack.mitre.org/techniques/T1053/005/ */
export function techniqueHref(id: string): string {
  return `https://attack.mitre.org/techniques/${normTech(id).replace(".", "/")}/`;
}

/** Name and tactics; an unknown sub-technique borrows its parent's tactics (and is daggered). */
export function techniqueInfo(id: string): Info | undefined {
  const k = normTech(id);
  const known = TECHNIQUES[k];
  if (known) return known;
  const parent = TECHNIQUES[k.split(".")[0]];
  if (parent) return { name: `${parent.name} (${k.slice(k.indexOf("."))})`, tactics: parent.tactics, dagger: true };
  return undefined;
}

type AttackTech = {
  id: string;
  name: string;
  href: string;
  /** To verify against v19 (renumbered, merged, or not in the table). */
  dagger: boolean;
  /** Other tactics this technique belongs to, in matrix order. */
  also: string[];
  /** Short rule ids that emitted it ("remote-thread"), most severe first. */
  rules: string[];
  maxSev: Severity;
};
type AttackTactic = { id: string; name: string; techs: AttackTech[] };
type AttackTable = { tactics: AttackTactic[]; techniqueCount: number };

const UNMAPPED = { id: "—", name: "Not in the v19 table" };

/** "injection.remote-thread" → "remote-thread". */
export const shortRule = (id: string): string => (id.includes(".") ? id.slice(id.indexOf(".") + 1) : id);

export function attackTable(tags: readonly Tag[]): AttackTable {
  // Most severe tags first, so each tactic lists its techniques by the strongest rule that cited them.
  const ranked = tags.map((t, i) => ({ t, i })).sort((a, b) => b.t.severity - a.t.severity || a.i - b.i);
  const techs = new Map<string, AttackTech & { primary: string }>();
  for (const { t } of ranked) {
    for (const raw of t.mitre_ids ?? []) {
      if (!raw?.trim()) continue;
      const id = normTech(raw);
      const rule = shortRule(t.id);
      const have = techs.get(id);
      if (have) {
        if (!have.rules.includes(rule)) have.rules.push(rule);
        if (t.severity > have.maxSev) have.maxSev = t.severity;
        continue;
      }
      const info = techniqueInfo(id);
      const tactics = info?.tactics ?? [];
      techs.set(id, {
        id,
        name: info?.name ?? "",
        href: techniqueHref(id),
        dagger: !info || !!info.dagger,
        also: tactics.slice(1).slice().sort((a, b) => tacticRank(a) - tacticRank(b)),
        rules: [rule],
        maxSev: t.severity,
        primary: tactics[0] ?? UNMAPPED.name,
      });
    }
  }
  const tactics: AttackTactic[] = [];
  for (const [tid, name] of TACTICS) {
    const list = [...techs.values()].filter((x) => x.primary === name);
    if (list.length) tactics.push({ id: tid, name, techs: list.map(strip) });
  }
  const unmapped = [...techs.values()].filter((x) => x.primary === UNMAPPED.name);
  if (unmapped.length) tactics.push({ id: UNMAPPED.id, name: UNMAPPED.name, techs: unmapped.map(strip) });
  return { tactics, techniqueCount: techs.size };
}

function tacticRank(name: string): number {
  const i = TACTICS.findIndex(([, n]) => n === name);
  return i < 0 ? TACTICS.length : i;
}

function strip({ primary: _primary, ...rest }: AttackTech & { primary: string }): AttackTech {
  void _primary;
  return rest;
}
